import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from '@/stores';
import { config } from '@/lib/config';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { BarChart3, Send, CheckCircle2, XCircle, Clock, Download, RefreshCw } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { toast } from 'sonner';
import type { NotificationHistory } from '@core/models';
import type { NotificationStatsResponse } from '@core';
import {
  NotificationStatusScheduled,
  NotificationStatusSending,
  NotificationStatusSent,
  NotificationStatusFailed,
  NotificationStatusCancelled,
} from '@core/models';

const statusLabels: Record<string, string> = {
  [NotificationStatusScheduled]: '配信予定',
  [NotificationStatusSending]: '配信中',
  [NotificationStatusSent]: '配信済み',
  [NotificationStatusFailed]: '失敗あり',
  [NotificationStatusCancelled]: 'キャンセル',
};

const typeLabels: Record<string, string> = {
  broadcast: '全体配信',
  targeted: 'ターゲット配信',
  test: 'テスト配信',
};

const NotificationStats: React.FC = observer(() => {
  const { tenantStore } = useStores();
  const [stats, setStats] = useState<NotificationStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const tenantId = tenantStore.selectedTenant?.id;

  const fetchStats = useCallback(async () => {
    if (!tenantId) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantId}/notifications/stats`,
        {
          credentials: 'include',
        }
      );

      if (response.ok) {
        setStats(await response.json());
      } else {
        toast.error('統計情報の取得に失敗しました');
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('統計情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const exportCsv = async () => {
    if (!tenantId) return;

    setExporting(true);
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tenants/${tenantId}/notifications/history`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        toast.error('履歴の取得に失敗しました');
        return;
      }

      const histories: NotificationHistory[] = await response.json();

      const header = ['id', 'title', 'type', 'status', 'target_count', 'delivered_count', 'failed_count', 'sent_at', 'completed_at'];
      const escapeCell = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
      const rows = histories.map((h) => [
        h.id,
        h.title,
        h.type,
        h.status || NotificationStatusSent,
        String(h.target_count),
        String(h.delivered_count),
        String(h.failed_count),
        formatDate(h.sent_at),
        formatDate(h.completed_at),
      ].map(escapeCell).join(','));

      const csv = `\uFEFF${[header.join(','), ...rows].join('\n')}`;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `notification_stats_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success('CSVをダウンロードしました');
    } catch (error) {
      console.error('Failed to export stats:', error);
      toast.error('エクスポートに失敗しました');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateString));
  };

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <RefreshCw className={`h-5 w-5 mr-2 animate-spin ${loading ? '' : 'hidden'}`} />
        統計情報を読み込み中...
      </div>
    );
  }

  const chartData = stats.daily.map((d) => ({
    date: d.date.slice(5), // MM-DD
    配信数: d.count,
    成功: d.delivered,
    失敗: d.failed,
  }));

  const byTypeRows = Object.entries(stats.by_type || {}).sort((a, b) => b[1] - a[1]);
  const byStatusRows = Object.entries(stats.by_status || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <BarChart3 className="h-6 w-6" />
          統計・分析
        </h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchStats} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            更新
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={exporting}>
            <Download className="h-4 w-4 mr-1" />
            配信実績CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Send className="h-4 w-4" />
              総配信数
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_count}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">総対象数</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.target_total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              配信成功
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{stats.delivered_total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <XCircle className="h-4 w-4 text-red-600" />
              配信失敗
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{stats.failed_total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Clock className="h-4 w-4" />
              成功率 / 予定
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.success_rate.toFixed(1)}%</div>
            <div className="text-xs text-muted-foreground">配信予定: {stats.scheduled_count}件</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>日別の配信状況 (直近30日)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} fontSize={11} tickLine={false} axisLine={false} />
                <RechartsTooltip />
                <Bar dataKey="配信数" fill="#94a3b8" radius={[3, 3, 0, 0]} />
                <Bar dataKey="成功" fill="#16a34a" radius={[3, 3, 0, 0]} />
                <Bar dataKey="失敗" fill="#dc2626" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">配信タイプ別</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>タイプ</TableHead>
                  <TableHead className="text-right">件数</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byTypeRows.map(([key, count]) => (
                  <TableRow key={key}>
                    <TableCell>{typeLabels[key] || key}</TableCell>
                    <TableCell className="text-right font-semibold">{count}</TableCell>
                  </TableRow>
                ))}
                {byTypeRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center py-6 text-muted-foreground">
                      データがありません
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">ステータス別</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ステータス</TableHead>
                  <TableHead className="text-right">件数</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byStatusRows.map(([key, count]) => (
                  <TableRow key={key}>
                    <TableCell>{statusLabels[key] || key}</TableCell>
                    <TableCell className="text-right font-semibold">{count}</TableCell>
                  </TableRow>
                ))}
                {byStatusRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center py-6 text-muted-foreground">
                      データがありません
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
});

export default NotificationStats;
